import React, { useEffect, useMemo } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { Observer } from 'mobx-react';
import { Navigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { createColumnHelper } from '@tanstack/react-table';
import AddToQueueOutlinedIcon from '@mui/icons-material/AddToQueueOutlined';

import { Button } from '@shared/components/Button';
import { SearchForm } from '@shared/components/SearchForm';
import { Table } from '@shared/components/Table';
import { ConfirmationModal } from '@shared/components/ConfirmationModal';
import { NoResultsView } from '@shared/components/NoResultsView';
import { RoutePaths } from '@core/routes/RoutePaths';
import {
    useDevicePairingUIStore,
    useManagedDevicesUIStore,
    useUserPermissionsUIStore,
} from '@core/useStores';
import { ManagedPairedDevice, PAIRING_STATUS_REVOKED } from 'AuthPart/DevicePairing/services';

import { PairingStatusFilter } from './PairingStatusFilter';
import { useStyles } from './ManagedDevicesPage.styles';
import { messages, pairingStatusLabel } from 'AuthPart/DevicePairing/devicePairing.messages';
import { ThemeProps } from '@styles/theme';

const columnHelper = createColumnHelper<ManagedPairedDevice>();

/**
 * Every paired device in the company, for the people who hand the headsets out.
 *
 * A paired device stays signed in indefinitely, which is the point of pairing and also the reason
 * somebody has to be able to see the whole set and end any of them. Without this page a headset
 * that walks out of the building is invisible until its owner happens to look at their profile.
 */
export const ManagedDevicesPage: React.FC = () => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const managedDevicesUIStore = useManagedDevicesUIStore();
    const devicePairingUIStore = useDevicePairingUIStore();
    const userPermissionsUIStore = useUserPermissionsUIStore();

    const canManage = userPermissionsUIStore.isSuperAdmin ||
        userPermissionsUIStore.isMultiAdmin ||
        userPermissionsUIStore.isAdmin ||
        userPermissionsUIStore.isManager;

    useEffect(() => {
        if (!canManage) {
            return undefined;
        }

        managedDevicesUIStore.start();
        managedDevicesUIStore.startPolling();

        // Coming back to the tab is the moment the list is most likely to be wrong and most likely
        // to be looked at, so catch up straight away rather than waiting out the interval.
        const onVisibilityChange = () => {
            if (!document.hidden) {
                managedDevicesUIStore.refresh();
            }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            managedDevicesUIStore.cleanUp();
        };
    }, [canManage]);

    const columns = useMemo(() => [
        columnHelper.accessor('userFullName', {
            id: 'userFullName',
            header: messages['managedDevices.column.user'],
            size: 22,
            meta: { cardRole: 'title' },
            cell: info => {
                const { userEmail } = info.row.original;

                // A request that was denied or expired before anyone claimed it has a device but
                // no person, and inventing one would be worse than an honest dash.
                if (!userEmail && !info.getValue()) {
                    return <span className={styles.userEmail}>{messages['managedDevices.noOwner']}</span>;
                }

                return (
                    <div className={styles.userCell}>
                        <span className={styles.userName}>{info.getValue() || userEmail}</span>
                        <span className={styles.userEmail}>{userEmail}</span>
                    </div>
                );
            },
        }),

        columnHelper.accessor('deviceName', {
            id: 'deviceName',
            header: messages['managedDevices.column.device'],
            size: 24,
            meta: { cardRole: 'subtitle' },
            cell: info => {
                const { deviceIdentifier } = info.row.original;

                return (
                    <div className={styles.deviceCell}>
                        <span>{info.getValue() || messages['managedDevices.unnamed']}</span>
                        {
                            // Truncated with the whole value on hover: these run to 99 characters,
                            // and a wrapped hash would push every other column off the row.
                            deviceIdentifier && (
                                <span className={styles.deviceIdentifier} title={deviceIdentifier}>
                                    {deviceIdentifier}
                                </span>
                            )
                        }
                    </div>
                );
            },
        }),

        columnHelper.accessor('devicePlatform', {
            id: 'devicePlatform',
            header: messages['managedDevices.column.platform'],
            size: 13,
            meta: { cardRole: 'badge' },
        }),

        columnHelper.accessor('applicationVersion', {
            id: 'applicationVersion',
            header: messages['managedDevices.column.version'],
            size: 11,
            meta: { cardRole: 'detail' },
        }),

        columnHelper.accessor('pairedAt', {
            id: 'pairedAt',
            header: messages['managedDevices.column.pairedAt'],
            size: 14,
            meta: { cardRole: 'detail' },
            cell: info => (info.getValue() ? moment(info.getValue()).format('LLL') : ''),
        }),

        columnHelper.display({
            id: 'pairedBy',
            header: messages['managedDevices.column.pairedBy'],
            size: 12,
            enableSorting: false,
            meta: { cardRole: 'detail' },
            cell: info => {
                const device = info.row.original;

                // The distinction worth surfacing: a device somebody set up for themselves is a
                // different thing from one an administrator handed them.
                return device.pairedOnBehalf
                    ? device.approvedByFullName || ''
                    : messages['managedDevices.pairedBy.self'];
            },
        }),

        columnHelper.display({
            id: 'actions',
            header: messages['managedDevices.column.actions'],
            size: 4,
            enableSorting: false,
            meta: { cardRole: 'actions' },
            cell: info => (
                <Observer>
                    {() => (
                        // Only a live pairing can be ended. Offering the button on a revoked or
                        // expired row would promise something the server would refuse.
                        info.row.original.isActive
                            ? (
                                <Button
                                    color="error"
                                    onClick={() => managedDevicesUIStore.askToRevoke(info.row.original.id)}
                                    disabled={managedDevicesUIStore.revokeStatus.isLoading}
                                >
                                    {messages['managedDevices.action.revoke']}
                                </Button>
                            )
                            : null
                    )}
                </Observer>
            ),
        }),
    ], [styles]);

    const statusColumn = useMemo(() => columnHelper.accessor('status', {
        id: 'status',
        header: messages['managedDevices.column.status'],
        size: 14,
        meta: { cardRole: 'badge' },
        cell: info => {
            const { isActive, status, endedAt } = info.row.original;

            // What happened and when it happened belong together — "Revoked" on its own leaves the
            // obvious next question unanswered, and a bare date in its own column leaves you
            // matching it back to a state.
            return (
                <div className={styles.statusWrapper}>
                    <span className={styles.statusCell}>
                        {
                            (isActive || status === PAIRING_STATUS_REVOKED) && (
                                <span
                                    className={classNames(styles.marker, {
                                        [styles.markerGood]: isActive,
                                        [styles.markerCritical]: status === PAIRING_STATUS_REVOKED,
                                    })}
                                />
                            )
                        }
                        {pairingStatusLabel(status)}
                    </span>

                    {
                        endedAt && (
                            <span className={styles.statusTime} title={moment(endedAt).format('LLL')}>
                                {moment(endedAt).format('lll')}
                            </span>
                        )
                    }
                </div>
            );
        },
    }), [styles]);

    const onFetchData = ({ pageIndex, sortBy }: { pageIndex: number, sortBy?: { id: string, desc: boolean }[] }) => {
        const sort = sortBy?.[0];

        managedDevicesUIStore.applyQuery(pageIndex, sort?.id || '', Number(sort?.desc ?? 1));
    };

    if (!canManage) {
        return <Navigate to={{ pathname: RoutePaths.profile }} replace />;
    }

    return (
        <Observer>
            {() => {
                const { devices, meta, status, isEmpty, isFiltered, revokeTarget, revokeError } = managedDevicesUIStore;

                // The status column only earns its width once more than one state can appear.
                const tableColumns = managedDevicesUIStore.canSeeHistory
                    ? [...columns.slice(0, 1), statusColumn, ...columns.slice(1)]
                    : columns;

                return (
                    <section className={styles.wrapper}>
                        <header className={styles.header}>
                            <div>
                                <h1 className={styles.title}>{messages['managedDevices.title']}</h1>
                                <p className={styles.subtitle}>{messages['managedDevices.subtitle']}</p>
                            </div>

                            <Button
                                startIcon={<AddToQueueOutlinedIcon />}
                                onClick={() => devicePairingUIStore.openModal()}
                            >
                                {messages['managedDevices.action.pairDevice']}
                            </Button>
                        </header>

                        <PairingStatusFilter />

                        <div className={styles.toolbar}>
                            <SearchForm
                                className={styles.search}
                                onSearchTextChanged={(text: string) => managedDevicesUIStore.setSearchText(text)}
                            />

                            <span className={styles.count}>
                                {messages['managedDevices.count'](meta.totalEntities)}
                            </span>
                        </div>

                        {
                            revokeError && (
                                <p className={styles.error}>{messages['managedDevices.revokeError']}</p>
                            )
                        }

                        {
                            isEmpty
                                ? (
                                    isFiltered
                                        ? <NoResultsView entityName="paired devices" />
                                        : <p className={styles.empty}>{messages['managedDevices.empty']}</p>
                                )
                                : (
                                    <div className={styles.tableContainer}>
                                        <Table
                                            data={devices}
                                            columns={tableColumns}
                                            fetchData={onFetchData}
                                            isLoading={status.isLoading}
                                            useSkeleton
                                            paginationStatus={{ ...meta }}
                                            getRowClassName={(row: any) =>
                                                row?.original?.isActive ? undefined : styles.inactiveRow}
                                        />
                                    </div>
                                )
                        }

                        <ConfirmationModal
                            isOpen={!!revokeTarget}
                            message={messages['managedDevices.revokeConfirm.message'](
                                revokeTarget?.userFullName || revokeTarget?.userEmail || ''
                            )}
                            note={messages['managedDevices.revokeConfirm.note']}
                            confirmText={messages['managedDevices.action.revoke']}
                            onDecline={() => managedDevicesUIStore.cancelRevoke()}
                            onConfirm={() => managedDevicesUIStore.confirmRevoke()}
                        />
                    </section>
                );
            }}
        </Observer>
    );
}
